import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CompanyLayout from '../components/CompanyLayout';
import { COMPANY_ROUTES } from '../constants';

/**
 * Job Posting Method Selection Page
 *
 * This page allows recruiters to choose between:
 * 1. Uploading a PDF job description (which will be processed by david-recruiter)
 * 2. Filling out the job posting form manually
 */
const JobPostingMethod: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedMethod, setSelectedMethod] = useState<'pdf' | 'form' | null>(null);

    // Get projectId from navigation state (if coming from a project)
    const projectId = (location.state as any)?.projectId;

    console.log('🔍 JobPostingMethod - projectId from state:', projectId);

    const handleMethodSelect = (method: 'pdf' | 'form') => {
        if (method === 'pdf') {
            console.log('🔍 JobPostingMethod - Passing projectId to David:', projectId);
            // Redirect to Talk to David page when PDF upload is selected
            navigate('/companies/talk-to-david', {
                state: {
                    projectId, // Pass project context to David
                    fromJobPosting: true
                }
            });
        } else {
            setSelectedMethod(method);
        }
    };

    const handleFormMethod = () => {
        navigate(COMPANY_ROUTES.JOB_POSTING, {
            state: { projectId }
        });
    };

    const handleBack = () => {
        // If we came from a specific project, go back to that project
        // Otherwise, go to the projects list
        if (projectId) {
            navigate(COMPANY_ROUTES.PROJECT_DETAILS(projectId));
        } else {
            navigate(COMPANY_ROUTES.PROJECTS);
        }
    };

    return (
        <CompanyLayout>
            {/* Back Button */}
            <button
                onClick={handleBack}
                className="mb-6 hover:opacity-70 transition-opacity"
            >
                <img src="/jobs/back_button.svg" alt="Back" className="w-6 h-6" />
            </button>

            {/* Header */}
            <div className="mb-8">
                <h1 className="text-[28px] font-semibold text-[#0B111E] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    How would you like to post your job?
                </h1>
                <p className="text-base text-[#515151]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Choose the method that works best for you
                </p>
            </div>

            {/* Method Selection Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* PDF Upload Method */}
                <button
                    onClick={() => handleMethodSelect('pdf')}
                    className={`bg-white rounded-2xl p-8 border-2 transition-all text-left hover:shadow-lg ${
                        selectedMethod === 'pdf'
                            ? 'border-[#015FA6] shadow-lg'
                            : 'border-[#EDEDED] hover:border-[#015FA6]'
                    }`}
                >
                    <div className="flex items-center justify-center w-16 h-16 bg-[#F0F7FF] rounded-lg mb-6">
                        <svg
                            width="32"
                            height="32"
                            viewBox="0 0 32 32"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M20 4H8C6.9 4 6 4.9 6 6V26C6 27.1 6.9 28 8 28H24C25.1 28 26 27.1 26 26V10L20 4ZM8 26V6H19V11H24V26H8Z"
                                fill="#015FA6"
                            />
                            <path
                                d="M12 18H20V20H12V18ZM12 22H20V24H12V22ZM12 14H16V16H12V14Z"
                                fill="#015FA6"
                            />
                        </svg>
                    </div>
                    <h2 className="text-xl font-semibold text-[#0B111E] mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Upload Job Description
                    </h2>
                    <p className="text-base text-[#757575] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Have an existing job description? Upload a PDF and let David help you refine it into a complete job posting.
                    </p>
                    <ul className="space-y-2 text-sm text-[#757575]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <li className="flex items-center gap-2">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path
                                    d="M6.66667 10.1147L12.7947 3.98599L13.7381 4.92866L6.66667 12L2.42267 7.75599L3.36534 6.81333L6.66667 10.1147Z"
                                    fill="#015FA6"
                                />
                            </svg>
                            Fast and convenient
                        </li>
                        <li className="flex items-center gap-2">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path
                                    d="M6.66667 10.1147L12.7947 3.98599L13.7381 4.92866L6.66667 12L2.42267 7.75599L3.36534 6.81333L6.66667 10.1147Z"
                                    fill="#015FA6"
                                />
                            </svg>
                            AI-assisted refinement
                        </li>
                        <li className="flex items-center gap-2">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path
                                    d="M6.66667 10.1147L12.7947 3.98599L13.7381 4.92866L6.66667 12L2.42267 7.75599L3.36534 6.81333L6.66667 10.1147Z"
                                    fill="#015FA6"
                                />
                            </svg>
                            Supports PDF, DOCX, DOC, TXT
                        </li>
                    </ul>
                </button>

                {/* Manual Form Method */}
                <button
                    onClick={() => handleMethodSelect('form')}
                    className={`bg-white rounded-2xl p-8 border-2 transition-all text-left hover:shadow-lg ${
                        selectedMethod === 'form'
                            ? 'border-[#015FA6] shadow-lg'
                            : 'border-[#EDEDED] hover:border-[#015FA6]'
                    }`}
                >
                    <div className="flex items-center justify-center w-16 h-16 bg-[#F0F7FF] rounded-lg mb-6">
                        <svg
                            width="32"
                            height="32"
                            viewBox="0 0 32 32"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M25.3333 5.33333H6.66667C5.2 5.33333 4 6.53333 4 8V24C4 25.4667 5.2 26.6667 6.66667 26.6667H25.3333C26.8 26.6667 28 25.4667 28 24V8C28 6.53333 26.8 5.33333 25.3333 5.33333ZM25.3333 24H6.66667V8H25.3333V24Z"
                                fill="#015FA6"
                            />
                            <path
                                d="M9.33333 12H22.6667V14.6667H9.33333V12ZM9.33333 17.3333H22.6667V20H9.33333V17.3333Z"
                                fill="#015FA6"
                            />
                        </svg>
                    </div>
                    <h2 className="text-xl font-semibold text-[#0B111E] mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Fill Out Form
                    </h2>
                    <p className="text-base text-[#757575] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Start from scratch and fill out our comprehensive job posting form with all the details.
                    </p>
                    <ul className="space-y-2 text-sm text-[#757575]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <li className="flex items-center gap-2">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path
                                    d="M6.66667 10.1147L12.7947 3.98599L13.7381 4.92866L6.66667 12L2.42267 7.75599L3.36534 6.81333L6.66667 10.1147Z"
                                    fill="#015FA6"
                                />
                            </svg>
                            Complete control
                        </li>
                        <li className="flex items-center gap-2">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path
                                    d="M6.66667 10.1147L12.7947 3.98599L13.7381 4.92866L6.66667 12L2.42267 7.75599L3.36534 6.81333L6.66667 10.1147Z"
                                    fill="#015FA6"
                                />
                            </svg>
                            Guided step-by-step
                        </li>
                        <li className="flex items-center gap-2">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path
                                    d="M6.66667 10.1147L12.7947 3.98599L13.7381 4.92866L6.66667 12L2.42267 7.75599L3.36534 6.81333L6.66667 10.1147Z"
                                    fill="#015FA6"
                                />
                            </svg>
                            No existing job description needed
                        </li>
                    </ul>
                </button>
            </div>

            {/* Form Method Section */}
            {selectedMethod === 'form' && (
                <div className="bg-white rounded-2xl p-8 border border-[#EDEDED]">
                    <h3 className="text-lg font-semibold text-[#0B111E] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Ready to Create Your Job Posting?
                    </h3>
                    <p className="text-base text-[#757575] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        You'll be guided through a comprehensive form to create your job posting with all the necessary details.
                    </p>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleFormMethod}
                            className="px-6 py-3 bg-[#015FA6] text-white font-medium rounded-lg hover:bg-[#014a85] transition-colors"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                            Start Filling Form
                        </button>
                        <button
                            onClick={() => setSelectedMethod(null)}
                            className="px-6 py-3 border border-[#EDEDED] text-[#0B111E] font-medium rounded-lg hover:bg-[#F4F4F4] transition-colors"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </CompanyLayout>
    );
};

export default JobPostingMethod;
